import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { MapPin } from "lucide-react";
import { SkyDropdown, SkyTooltip } from "@skypointcloud/skypoint-ui";
import { SCOPE_URL_PARAMS } from "../../../../../shared/types/accessScope";
import { useMsalAuth } from "../../../../hooks/useMsalAuth";
import {
  ALL,
  locationOptions,
  parseLocationValue,
  showLocationDropdown,
  validLocation,
} from "../../../../components/shared/scope/scopeFilterState";
import { writeLocationChoice } from "../../../../components/shared/scope/locationStore";
import { useLocationScope } from "../../../../components/shared/scope/useScopeFilter";
import { ScopeOptionLabel } from "../../../../components/shared/scope/ScopeOptionLabel";
import { locationFilterAppliesTo, NOT_FILTERED_TOOLTIP } from "./locationSwitcher";
import { useGetMyAccessScopeQuery } from "../../../../store/api/accessScopeApi";
import { useGetMyPermissionsQuery } from "../../../../store/api/rbacApi";
import { useSkipsHireShellReads } from "../../../../hooks/useSkipsHireShellReads";

/**
 * On a dark header the dropdown's own colours (dark text, grey border) don't
 * read, so the trigger takes the inverted surface's tokens. The listbox opens in
 * a portal on the page, where the default colours are right.
 *
 * The label is there for the accessible name only (SkyDropdown renders it as
 * `<label for>` and forwards no aria-label). It is not shown: the value already
 * reads "All locations" or a community name, and the pin says which control this
 * is (D34).
 */
/**
 * The trigger and its menu are the same width, so the menu can't overhang it
 * (D35). The class is a literal because Tailwind reads these at build time.
 */
export const CONTROL_WIDTH = "180px";

const HEADER_FIELD = [
  // A descendant selector, not a child one: the DS may nest the label deeper.
  "[&_label]:sr-only",
  "[&_[role=combobox]]:w-[180px]! [&_[role=combobox]]:border-header-foreground/30! [&_[role=combobox]]:text-header-foreground!",
  "[&_[role=combobox]:hover:not(:disabled)]:border-header-foreground/60!",
  "[&_[role=combobox]_svg]:text-header-foreground/70!",
].join(" ");

/** How long who-the-user-is is trusted before their next move re-reads it. */
export const ACCESS_FRESH_MS = 60_000;

/**
 * Re-reads the caller's access scope and permissions when they move to another
 * page, if what the app holds is over a minute old. Access can change while
 * someone is signed in: an admin narrows their communities, changes their role,
 * deactivates a location. The server applies it at once; this lets the header's
 * Location list, the nav and route guards, and every whole-instance control
 * catch up on the next move, rather than at the next reload. Everything reading
 * these two queries shares the refreshed data. Mounted once, by the header.
 */
export function useFreshAccess() {
  const { getInstanceId } = useMsalAuth();
  // No hire reads in skyPerformance IT's shell for someone without a hire role (spec 2026-10-26).
  const skip = useSkipsHireShellReads();
  const instanceId = skip ? "" : getInstanceId() || "";
  const { pathname } = useLocation();
  const scope = useGetMyAccessScopeQuery(instanceId, { skip: !instanceId });
  // skipToken, not `undefined`: an `undefined` arg is its own cache entry, which the base query
  // fills from the URL's instance, a second my-permissions request beside the guard's (review 03
  // P2-2). Outside the IT shell, unchanged.
  const permissions = useGetMyPermissionsQuery(skip ? skipToken : instanceId || undefined);

  useEffect(() => {
    // Only a query that has answered can be refetched (RTK throws otherwise).
    const stale = (at: number | undefined) => at !== undefined && Date.now() - at > ACCESS_FRESH_MS;
    if (stale(scope.fulfilledTimeStamp)) void scope.refetch();
    if (stale(permissions.fulfilledTimeStamp)) void permissions.refetch();
    // On a move only: the timestamps change with every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}

/**
 * Takes a `?loc=` deep link into the per-instance choice once the caller's scope
 * has loaded, then removes it from the URL so a later change isn't undone by a
 * reload. A value that is no longer a choice falls back to "all", silently (D27).
 * Mounted once, by the header.
 */
export function useLocationDeepLink() {
  const { getInstanceId } = useMsalAuth();
  const instanceId = useSkipsHireShellReads() ? "" : getInstanceId() || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const { scope } = useLocationScope(instanceId);
  const urlLocation = searchParams.get(SCOPE_URL_PARAMS.location);

  useEffect(() => {
    // A failed scope load can't tell a stale link from a good one: leave it be.
    if (!instanceId || !scope || urlLocation === null) return;
    writeLocationChoice(instanceId, validLocation(parseLocationValue(urlLocation), scope));
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(SCOPE_URL_PARAMS.location);
        return next;
      },
      { replace: true },
    );
  }, [instanceId, scope, urlLocation, setSearchParams]);
}

/** Whether the switcher shows for this person in this instance (not whether it is enabled). */
export function useLocationSwitcherVisible(): boolean {
  const { getInstanceId } = useMsalAuth();
  const skip = useSkipsHireShellReads();
  const { ready, scope } = useLocationScope(skip ? "" : getInstanceId() || "");
  return ready && showLocationDropdown(scope);
}

interface HeaderLocationSwitcherProps {
  /** "header" on the dark desktop bar; "sheet" in the mobile side menu. */
  variant?: "header" | "sheet";
}

/**
 * The Location switcher (D27): one choice per instance, applied by every list
 * page. Hidden for roles the filter doesn't apply to and for people with a single
 * community. Shown but disabled on pages it doesn't filter.
 */
export const HeaderLocationSwitcher = ({ variant = "header" }: HeaderLocationSwitcherProps) => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { getInstanceId } = useMsalAuth();
  const instanceId = getInstanceId() || "";
  const { ready, scope, location } = useLocationScope(instanceId, searchParams);

  if (!ready || !scope || !showLocationDropdown(scope)) return null;

  const applies = locationFilterAppliesTo(pathname);
  const dropdown = (
    <SkyDropdown
      label="Location"
      size="medium"
      options={locationOptions(scope)}
      selectedOptions={[location]}
      disabled={!applies}
      listboxMaxHeight="360px"
      listboxMinWidth={CONTROL_WIDTH}
      className={variant === "header" ? HEADER_FIELD : undefined}
      // A flat list (D36): a group row carries its own mark instead of a heading.
      optionTemplate={(option) => <ScopeOptionLabel option={option} />}
      selectedOptionTemplate={
        variant === "header"
          ? (selected) => (
              // The control is narrow (D35), so a long community name truncates
              // here and the full one is the title. The menu's search finds it.
              <span
                className="flex min-w-0 items-center gap-1.5"
                title={selected[0]?.text ?? ""}
              >
                {/*
                  The trailing `!` is not decoration. Unlayered CSS in the bundle sets this
                  svg to 20px and silently beats a plain `h-4 w-4`, which is why the pin looked
                  oversized next to a 12px label. 14px sits on the label's own line box.
                */}
                <MapPin className="h-3.5! w-3.5! shrink-0" />
                <span className="truncate">{selected[0]?.text ?? ""}</span>
              </span>
            )
          : undefined
      }
      testId={`location-switcher-${variant}`}
      onOptionSelect={(values) =>
        writeLocationChoice(instanceId, parseLocationValue(values[0]) ?? ALL)
      }
    />
  );

  if (applies) return dropdown;
  return (
    <SkyTooltip content={NOT_FILTERED_TOOLTIP}>
      {/* A disabled control fires no pointer events, so the tooltip needs a wrapper. */}
      <span className="inline-flex">{dropdown}</span>
    </SkyTooltip>
  );
};
